// Stale-chunk recovery after a deploy.
//
// The portal lazy-loads heavy chunks (DashboardEditor, ReportBuilder) via
// React.lazy(() => import(...)). Vite compiles those into preload calls with
// content-hashed filenames. When a new frontend build is deployed, the old
// hashed chunk names disappear; a browser still running the previous
// index.html then requests a filename that no longer exists. The SPA host
// serves index.html (text/html) for the missing .js, the module load is
// blocked, and the user hits the error boundary ("Something went wrong!")
// until they manually hard-reload.
//
// Vite emits a `vite:preloadError` event on exactly this failure. We reload
// once to pull the fresh index.html (with the correct chunk hashes). A
// time-window guard prevents a reload loop: if a second preload error fires
// within RELOAD_WINDOW_MS, the chunk is genuinely broken (not just stale), so
// we let it fall through to the error boundary instead of reloading forever.

const RELOAD_KEY = "vite-preload-reloaded-at";
const RELOAD_WINDOW_MS = 10_000;

export function installChunkReloadHandler(target: Window = window): void {
  target.addEventListener("vite:preloadError", () => {
    // Never reload under the prerenderer (it injects this marker before any
    // app code). The dist being served is immutable mid-build, so a reload
    // cannot fix anything - but it CAN land between the prerender-ready
    // dispatch and the snapshot, in which case the snapshot captures the
    // fresh, Helmet-less shell - a snapshot with none of the route's own head.
    // Suspected in the /status prerender failures of 2026-08-03, though the
    // proven cause there was a render crash; this guard stands on its own
    // merits either way.
    if ((target as { __PRERENDER_INJECTED?: unknown }).__PRERENDER_INJECTED) {
      return;
    }

    const last = Number(target.sessionStorage.getItem(RELOAD_KEY) ?? "0");
    const now = Date.now();

    if (now - last > RELOAD_WINDOW_MS) {
      target.sessionStorage.setItem(RELOAD_KEY, String(now));
      target.location.reload();
    }
    // Otherwise we already reloaded moments ago and the chunk is still
    // missing — a real failure. Do nothing and let the error boundary show.
  });
}
